import { DESTINATION_COUNTRIES_OPTIONS_LIST } from '../../../common/constants';

export const SUPPORTED_DESTINATIONS = DESTINATION_COUNTRIES_OPTIONS_LIST.map(
  country => country.value,
);

type GicVendor = 'bmo' | 'rbc' | 'td';
type ForexVendor = 'reeudo' | 'flywire';
type BankingVendor = 'bmo' | 'expatrio' | 'coracle';
type VisaVendor =
  | 'one vasco'
  | 'us mock interview'
  | 'uk credibility interview'
  | 'sop generator'
  | 'canadian visa calculator';

type GlobalVendor = { isGlobal: true };
type RestrictedVendor = { supportedNationalities: string[]; supportedDestinations?: string[] };

export const SERVICE_FILTER_RULES = {
  gic: {
    supportedDestinations: ['CA'],
    isGlobal: false,
    vendors: {
      bmo: {
        supportedNationalities: [
          'AG',
          'CO',
          'CN',
          'CR',
          'IN',
          'MA',
          'PK',
          'PE',
          'PH',
          'VC',
          'SN',
          'VN',
        ],
      } as RestrictedVendor,
      rbc: {
        supportedNationalities: ['BR', 'CO', 'CR', 'PE', 'IN', 'CN', 'PH', 'VN'],
      } as RestrictedVendor,
      td: {
        supportedNationalities: ['CN', 'IN', 'MA', 'PK', 'PH', 'SN', 'VN'],
      } as RestrictedVendor,
    },
  },
  loan: {
    isGlobal: true,
  },
  test: {
    isGlobal: true,
  },
  accommodation: {
    isGlobal: true,
  },
  visa: {
    isGlobal: false,
    vendors: {
      'one vasco': {
        isGlobal: true,
      } as GlobalVendor,
      'us mock interview': {
        supportedDestinations: ['US'],
      } as RestrictedVendor,
      'uk credibility interview': {
        supportedDestinations: ['GB'],
      } as RestrictedVendor,
      'sop generator': {
        isGlobal: true,
      } as GlobalVendor,
      'canadian visa calculator': {
        supportedDestinations: ['CA'],
      } as RestrictedVendor,
    },
  },
  banking: {
    isGlobal: false,
    supportedDestinations: ['CA', 'DE'],
    vendors: {
      bmo: {
        supportedNationalities: [
          'AG',
          'CO',
          'CN',
          'CR',
          'IN',
          'MA',
          'PK',
          'PE',
          'PH',
          'VC',
          'SN',
          'VN',
        ],
        supportedDestinations: ['CA'],
      } as RestrictedVendor,
      expatrio: {
        supportedDestinations: ['DE'],
      } as RestrictedVendor,
      coracle: {
        supportedDestinations: ['DE'],
      } as RestrictedVendor,
    },
  },
  forex: {
    vendors: {
      reeudo: {
        supportedNationalities: ['IN', 'NP', 'PK'],
      } as RestrictedVendor,
      flywire: {
        isGlobal: true,
      } as GlobalVendor,
    },
  },
  arrival_kits: {
    vendors: {
      bokz: {
        supportedDestinations: [
          'AL',
          'AD',
          'AM',
          'AT',
          'AZ',
          'BY',
          'BE',
          'BA',
          'BG',
          'HR',
          'CY',
          'CZ',
          'DK',
          'EE',
          'FI',
          'FR',
          'GE',
          'DE',
          'GR',
          'HU',
          'IS',
          'IE',
          'IT',
          'LV',
          'LI',
          'LT',
          'LU',
          'MT',
          'MD',
          'MC',
          'NL',
          'NO',
          'PL',
          'PT',
          'RO',
          'RS',
          'SK',
          'SI',
          'ES',
          'SE',
          'CH',
          'TR',
          'UA',
          'GB',
        ],
      },
      mydormstore: {
        supportedDestinations: ['CA'],
      },
    } as Record<string, { supportedDestinations: string[] }>,
  },
};

export type CardData = {
  cardKey: string;
  service: string;
  vendor: string;
  badge?: string;
  image: string;
  isImageForAvatar?: boolean;
  title?: string;
  isTitleUnderImage?: boolean;
  description: string;
  marketingTemplateLink?: {
    url: string;
    text: string;
  };
  shareLinkButtonName: string;
  applyButtonName: string;
  applyButtonCTAUrl?: string;
  vendorUrl: string;
};

export function applyCardFilters(
  cards: CardData[],
  serviceType: string,
  filters: { nationality?: string; destination?: string[] },
): CardData[] {
  const { nationality, destination = [] } = filters;
  const nationalityArray = nationality ? [nationality] : [];

  if (nationalityArray.length === 0 && destination.length === 0) {
    return cards;
  }

  return cards.filter(card => {
    if (['loan', 'accommodation', 'test'].includes(serviceType)) {
      return true;
    }

    if (serviceType === 'visa') {
      return handleVisaFiltering(card, destination);
    }

    if (serviceType === 'academic tools') {
      return handleAcademicToolsFiltering(card, destination);
    }

    if (serviceType === 'forex' && card.vendor) {
      return handleForexFiltering(card, nationalityArray);
    }

    if (serviceType === 'gic') {
      return handleGicFiltering(card, nationalityArray, destination);
    }

    if (serviceType === 'banking') {
      return handleBankingFiltering(card, nationalityArray, destination);
    }

    if (serviceType === 'arrival_kits') {
      return handleArrivalKitsFiltering(card, destination);
    }

    return true;
  });
}

function handleForexFiltering(card: CardData, nationality: string[]): boolean {
  const vendorKey = card.vendor.toLowerCase() as ForexVendor;

  if (vendorKey && ['reeudo', 'flywire'].includes(vendorKey)) {
    const forexRules = SERVICE_FILTER_RULES.forex.vendors[vendorKey];

    if ('isGlobal' in forexRules && forexRules.isGlobal) {
      return true;
    }

    if ('supportedNationalities' in forexRules && nationality.length > 0) {
      return nationality.some(nat => forexRules.supportedNationalities.includes(nat));
    }

    return nationality.length === 0;
  }

  return false;
}

function handleGicFiltering(card: CardData, nationality: string[], destination: string[]): boolean {
  if (destination.length > 0 && !destination.includes('CA')) {
    return false;
  }

  if (card.vendor && nationality.length > 0) {
    const vendorKey = card.vendor.toLowerCase() as GicVendor;
    if (vendorKey && ['bmo', 'rbc', 'td'].includes(vendorKey)) {
      const gicRules = SERVICE_FILTER_RULES.gic.vendors[vendorKey];

      if ('isGlobal' in gicRules && gicRules.isGlobal) {
        return true;
      }

      if ('supportedNationalities' in gicRules) {
        return nationality.some(nat => gicRules.supportedNationalities.includes(nat));
      }
    }
  }

  return nationality.length === 0;
}

function handleBankingFiltering(
  card: CardData,
  nationality: string[],
  destination: string[],
): boolean {
  const bankingRules = SERVICE_FILTER_RULES.banking;

  if (destination.length > 0 && 'supportedDestinations' in bankingRules) {
    const hasValidDestination = destination.some(dest =>
      bankingRules.supportedDestinations.includes(dest),
    );
    if (!hasValidDestination) return false;
  }

  if (card.vendor) {
    const vendorKey = card.vendor.toLowerCase() as BankingVendor;

    if (vendorKey === 'bmo') {
      const vendorRules = bankingRules.vendors[vendorKey];
      const isEligibleNationality =
        !nationality ||
        nationality?.length === 0 ||
        nationality.some(nat => vendorRules.supportedNationalities?.includes(nat));
      const isEligibleDestination =
        !destination ||
        destination?.length === 0 ||
        destination.some(nat => vendorRules.supportedDestinations?.includes(nat));

      return isEligibleNationality && isEligibleDestination;
    }

    if (['expatrio', 'coracle'].includes(vendorKey)) {
      const vendorRules = bankingRules.vendors[vendorKey];

      return (
        destination.length === 0 ||
        destination.some(nat => vendorRules.supportedDestinations?.includes(nat))
      );
    }
  }

  return true;
}

function handleArrivalKitsFiltering(card: CardData, destination: string[]): boolean {
  const vendorKey = card.vendor.toLowerCase();
  const arrivalKitsVendors = SERVICE_FILTER_RULES.arrival_kits.vendors as Record<
    string,
    { supportedDestinations: string[] }
  >;
  const arrivalKitsRules = arrivalKitsVendors[vendorKey];
  if (arrivalKitsRules && destination.length > 0) {
    return destination.some(dest => arrivalKitsRules.supportedDestinations.includes(dest));
  }
  return destination.length === 0;
}

function handleVisaFiltering(card: CardData, destination: string[]): boolean {
  const vendorKey = card.vendor.toLowerCase() as VisaVendor;
  if (destination.length === 0) {
    return true;
  }
  if (vendorKey && Object.keys(SERVICE_FILTER_RULES.visa.vendors).includes(vendorKey)) {
    const visaRules = SERVICE_FILTER_RULES.visa.vendors[vendorKey];

    if ('isGlobal' in visaRules) {
      return true;
    }

    const restrictedRules = visaRules as RestrictedVendor;
    return destination.some(dest => restrictedRules.supportedDestinations?.includes(dest));
  }
  return false;
}

function handleAcademicToolsFiltering(card: CardData, destination: string[]): boolean {
  // todo: once the logic of academic tools starts to differ from visa, we can implement it here
  return handleVisaFiltering(card, destination);
}
